import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import HeroGallery from '../components/HeroGallery';
import PriceCard from '../components/PriceCard';
import { Calendar, X, Utensils, Package } from 'lucide-react';

const Home: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    { icon: <Calendar size={28} />, text: t('info.open7days') },
    { icon: <X size={28} />, text: t('info.noDelivery') },
    { icon: <Utensils size={28} />, text: t('info.eatInRestaurant') },
    { icon: <Package size={28} />, text: t('info.takeaway') }
  ];

  return (
    <>
      <div className="animate-fadeIn">
        <Hero />
      </div>

      {/* Gallery Section */}
      <div className="animate-fadeInUp animation-delay-200">
        <HeroGallery />
      </div>

      {/* Prices Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-koi-light relative animate-fadeInUp animation-delay-400">
        {/* Fade from white gradient at the top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white to-transparent" />
        {/* Fade to white gradient at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
        <div className="section-padding relative z-10">
          <div className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-koi-red text-lg tracking-luxury uppercase font-body mb-4">{t('prices.section')}</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-gray-800">
              {t('prices.title')}
            </h2>
            <div className="h-px bg-koi-red mx-auto w-20" />
          </div>

          {/* Adult Prices */}
          <div className="mb-8">
            <h3 className="text-xl font-display text-koi-red text-center mb-6">{t('prices.adultTitle')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-4xl mx-auto">
              <div>
                <PriceCard title={t('prices.lunchMonFri')} price="€17.90" />
              </div>
              <div>
                <PriceCard title={t('prices.dinnerMonFri')} price="€24.90" />
              </div>
              <div>
                <PriceCard title={t('prices.weekendHoliday')} price="€25.90" />
              </div>
            </div>
          </div>

          {/* Children Prices */}
          <div>
            <h3 className="text-xl font-display text-koi-red text-center mb-6">{t('prices.childTitle')}</h3>
            
            {/* 10-12 Years */}
            <div className="mb-8">
              <h4 className="text-lg font-display text-gray-800 text-center mb-4">{t('prices.children1012')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-4xl mx-auto">
                <div>
                  <PriceCard title={t('prices.lunchMonFri')} price="€13.90" />
                </div>
                <div>
                  <PriceCard title={t('prices.dinnerMonFri')} price="€19.90" />
                </div>
                <div>
                  <PriceCard title={t('prices.weekendHoliday')} price="€20.90" />
                </div>
              </div>
            </div>
            
            {/* 3-10 Years */}
            <div className="mb-8">
              <h4 className="text-lg font-display text-gray-800 text-center mb-4">{t('prices.children310')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-4xl mx-auto">
                <div>
                  <PriceCard title={t('prices.lunchMonFri')} price="€8.90" />
                </div>
                <div>
                  <PriceCard title={t('prices.dinnerMonFri')} price="€10.00" />
                </div>
                <div>
                  <PriceCard title={t('prices.weekendHoliday')} price="€11.90" />
                </div>
              </div>
            </div>
            
            {/* Under 3 */}
            <div className="text-center">
              <h4 className="text-lg font-display text-gray-800 mb-4">{t('prices.childrenUnder3')}</h4>
              <p className="text-2xl font-bold text-koi-red">{t('prices.free')}</p>
            </div>
          </div>

          <p className="text-center text-gray-600 mt-8 text-sm font-body italic">
            {t('prices.disclaimer')}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 lg:py-32 relative">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/features-background.jpg)'
          }}
        />
        {/* White overlay to maintain readability */}
        <div className="absolute inset-0 bg-white/80" />
        {/* Top fade transition from white */}
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-white to-transparent" />
        {/* Bottom fade transition to white */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
        
        <div className="section-padding relative z-10">
          <div className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-koi-red text-lg tracking-luxury uppercase font-body mb-4">{t('info.services')}</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-gray-800">
              {t('info.title')}
            </h2>
            <div className="h-px bg-koi-red mx-auto w-20" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-6 luxury-card p-8 hover-lift group"
              >
                <div className="text-koi-red group-hover:text-gray-800 transition-colors duration-300">{feature.icon}</div>
                <p className="text-gray-700 font-body text-lg">{feature.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/menu">
              <button className="luxury-button">
                {t('hero.cta')}
              </button>
            </Link>
          </div>
        </div>
      </section>


      {/* Transition Section */}
      <div className="h-16 bg-gradient-to-b from-white to-koi-light/40" />
      
      {/* CTA Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-white text-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-koi-light opacity-30" />
        {/* Bottom fade transition to footer */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-koi-light to-transparent" />
        <div className="section-padding text-center relative z-10">
          <div>
            <div className="h-px bg-koi-red mx-auto mb-8 w-16" />
            
            <h2 className="text-display-md md:text-display-lg font-display mb-8 text-gray-800">
              {t('cta.title')}
            </h2>
            <p className="text-xl mb-12 max-w-3xl mx-auto font-body leading-relaxed text-gray-700">
              {t('cta.text')}
            </p>
            <a
              href="https://www.google.com/maps?q=4+Rue+Willy+Bandt,+25300+Pontarlier,+France"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="bg-koi-red text-white px-10 py-5 font-display tracking-wider uppercase text-sm hover:bg-red-700 transition-all duration-500 border border-koi-red">
                {t('cta.button')}
              </button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;